// Backlog 072-D — `~/.cloudgrid/deploys.log` retention.
//
// One line per terminal-state plug event. Lets devs `tail
// ~/.cloudgrid/deploys.log` to see what happened while they were away.
// Rolled at ~1 MB to `deploys.log.1` (truncate-on-rotation; no compression).
//
// Format (whitespace-aligned, single line per event):
//
//   2026-05-04T09:35:12Z  sites-platform-e297  failure  cloud_build  4m 18s  build_id=248c70c3...  ERROR=BUILD_FAILED
//   2026-05-04T09:42:01Z  sites-platform-e297  success  -            5m 12s  -                     -
//
// File I/O is sync — append is small (one line) and the verb is already
// in its terminal state path. Failures are swallowed: missing dir gets
// created, but anything beyond that (perm denied, full disk) silently
// no-ops so the plug verb's exit semantics aren't affected.

import {
  appendFileSync,
  existsSync,
  mkdirSync,
  readFileSync,
  renameSync,
  statSync,
  writeFileSync,
} from 'node:fs';
import { join } from 'node:path';

import { getCloudGridHome } from '../home.js';

/** ~1 MB — small enough to keep tail snappy, big enough for ~5k lines. */
export const DEFAULT_ROTATE_BYTES = 1024 * 1024;

export type DeployOutcome = 'success' | 'failure';

export interface DeployLogEntry {
  /** ISO 8601 — caller decides clock for testability. */
  ts: string;
  /** Entity slug (yaml `name:` or fallback to entity_id). */
  slug: string;
  outcome: DeployOutcome;
  /** Failed step name on failure; '-' on success. */
  step: string | null;
  /** Wall-clock elapsed ms — formatted as `Xm Ys` for the line. */
  elapsedMs: number;
  /** Cloud Build id when known; rendered as `build_id=<8-char-prefix>...`. */
  buildId?: string | null;
  /** Structured error code on failure (e.g. `BUILD_FAILED`). */
  errorCode?: string | null;
}

export interface AppendDeployLogDeps {
  /**
   * Override the home directory. Tests pass a temp dir; production reads
   * from `getCloudGridHome()`. The function CREATES the directory if it
   * doesn't exist (under typical first-run conditions where ~/.cloudgrid/
   * may not yet exist for users who skipped login).
   */
  home?: string;
  /** Override the rotation threshold for tests. */
  rotateBytes?: number;
  /** Override the clock — the entry's `ts` field already lets the caller
   *  set the timestamp; this hook is reserved for future use. */
}

/**
 * Format ms → `Xm Ys` (or `Ys` when under a minute). Mirrors the kickoff
 * spec's example `4m 18s`.
 */
export function formatElapsedHuman(ms: number): string {
  if (ms < 0 || !Number.isFinite(ms)) return '-';
  const totalS = Math.round(ms / 1000);
  if (totalS < 60) return `${totalS}s`;
  const m = Math.floor(totalS / 60);
  const s = totalS % 60;
  return `${m}m ${s}s`;
}

/**
 * Render one log line from an entry. No trailing newline — caller appends
 * one. Public for tests; the verb code goes through `appendDeployLog`.
 */
export function formatDeployLogLine(entry: DeployLogEntry): string {
  const slug = entry.slug.padEnd(20);
  const outcome = entry.outcome.padEnd(7);
  const step = (entry.step ?? '-').padEnd(14);
  const elapsed = formatElapsedHuman(entry.elapsedMs).padEnd(8);
  const buildIdField = entry.buildId
    ? `build_id=${entry.buildId.slice(0, 8)}...`
    : '-';
  const buildPadded = buildIdField.padEnd(20);
  const errField = entry.errorCode ? `ERROR=${entry.errorCode}` : '-';
  return `${entry.ts}  ${slug}  ${outcome}  ${step}  ${elapsed}  ${buildPadded}  ${errField}`;
}

/**
 * Roll the log file to `deploys.log.1` if it has grown past the threshold.
 * Truncate-on-rotation (no compression, no cascading numbered rolls). Best
 * effort — any I/O failure is swallowed.
 */
function rotateIfNeeded(logPath: string, rotatedPath: string, threshold: number): void {
  if (!existsSync(logPath)) return;
  let size = 0;
  try {
    size = statSync(logPath).size;
  } catch {
    return;
  }
  if (size < threshold) return;
  try {
    // Move current → .1 (overwrites any prior .1, intentionally — we only
    // keep one rotated copy).
    if (existsSync(rotatedPath)) {
      // `renameSync` overwrites on Linux/macOS but throws on Windows when
      // the destination exists. Read+write the destination first to remove
      // the prior copy without depending on platform-specific rename.
      writeFileSync(rotatedPath, readFileSync(logPath));
    } else {
      renameSync(logPath, rotatedPath);
    }
    // After a copy-based "move", truncate the original. After a real
    // rename, the file is gone and the next append will recreate it.
    if (existsSync(logPath)) {
      writeFileSync(logPath, '');
    }
  } catch {
    // Quiet — rotation failure is not user-visible.
  }
}

/**
 * Append one line to `<home>/deploys.log`. Idempotently creates the
 * directory and rotates when the file grows past the threshold. Never
 * throws — best-effort, telemetry-grade write.
 */
export function appendDeployLog(
  entry: DeployLogEntry,
  deps: AppendDeployLogDeps = {},
): void {
  const home = deps.home ?? getCloudGridHome();
  const logPath = join(home, 'deploys.log');
  const rotatedPath = join(home, 'deploys.log.1');
  const threshold = deps.rotateBytes ?? DEFAULT_ROTATE_BYTES;

  try {
    if (!existsSync(home)) mkdirSync(home, { recursive: true });
  } catch {
    return;
  }

  rotateIfNeeded(logPath, rotatedPath, threshold);

  const line = formatDeployLogLine(entry) + '\n';
  try {
    appendFileSync(logPath, line, { encoding: 'utf-8' });
  } catch {
    // Disk full / readonly fs / perms — drop quietly. The user still saw
    // the result on stdout.
  }
}
